/**
 * shared/war_authority.ts — CLIENT MIRROR of server/src/lib/war_authority.ts (space campaign slice D, 2026-09-30;
 * the server copy is authoritative — keep them byte-identical below the headers; verify:war-authority compares them).
 * THE COMBAT AUTHORITY PILOT (report §9.3, §21.1–21.5): the fleet war's named NPC lives (capitals + escorts) carry
 * SERVER hp, and a pilot's fire reaches them as a validated INTENT — never a damage number, never a kill claim. This
 * file is the pure half: the war's frame and capital seats (the renderer's own arithmetic — the server derives the
 * seat a client draws), the capital kinematic step the renderer runs (measured, never trusted), the certified proxies
 * and hp, the weapons, the fit (the combat subset of the client's shipStats), the shot judge, the receive window, the
 * rate buckets, the theatre's movement envelope. Pure.
 */
import { warH } from './space_stations';
import { BATTLE_CAPS, BATTLE_PERIOD_S, BATTLE_END_S, WAR_ZONE_M, ESCORT_KEY, type EscortClass } from './space_campaign';
import { isSandboxCyc } from './campaign_contract';

// ── THE ROLLOUT ──────────────────────────────────────────────────────────────
/** Read ONCE, when a campaign opens (an active campaign finishes on the rules it opened under — report §9.6 step 4). */
export const WAR_AUTHORITY_FLAG = 'combat_authority_v1';
/** The authority rules a pinned campaign runs; a shot carrying another version is refused (a stale client). */
export const WAR_AUTHORITY_VERSION = 1;

// ── THE WAR'S CLOCK, FRAME AND SEATS (the renderer's arithmetic — SpaceModule delegates here) ──────────────────────
export interface V3 { x: number; y: number; z: number }
/** War time (s) — the client draws a live war on the wall clock (`now − cyc·3600`); a harness SANDBOX war (a sentinel
 *  cycle no clock reaches) is drawn from its own opening (the probe anchors its forced window there). */
export function warTimeS(cyc: number, openedAtS: number, nowS: number): number {
  return isSandboxCyc(cyc) ? nowS - openedAtS : nowS - cyc * BATTLE_PERIOD_S;
}
/** The wall axis `ax` (side 0's wall at +ax) and the lateral `az`, hashed per cycle. */
export function warFrame(cyc: number): { ax: V3; az: V3 } {
  const th = warH(`bw${cyc}`) * Math.PI;
  return { ax: { x: Math.cos(th), y: 0, z: Math.sin(th) }, az: { x: -Math.sin(th), y: 0, z: Math.cos(th) } };
}
/** One capital's seat in its wall (row depth, height, lateral, sway phase) and its hashed warp-in second. */
export interface WarCapSeat { arr: number; dx: number; by: number; bz: number; sway: number }
/** Both walls' seats, each SORTED BY ARRIVAL: roster slot k is the k-th capital to warp in (the count-packed warp-in). */
export function warCapSeats(cyc: number): [WarCapSeat[], WarCapSeat[]] {
  return [0, 1].map((side) => Array.from({ length: BATTLE_CAPS }, (_, i) => {
    const s = `c${cyc}s${side}i${i}`;
    const row = Math.floor(i / 5), col = i % 5;
    return {
      arr: warH(s + 'a') * 8,
      dx: row * 4200 + (warH(s + 'x') - 0.5) * 1300,
      by: (row === 0 ? -1 : 1) * 900 + (warH(s + 'y') - 0.5) * 1100,
      bz: (col - 2) * 5200 + (warH(s + 'z') - 0.5) * 1500,
      sway: warH(s + 'w') * Math.PI * 2,
    };
  }).sort((a, z) => a.arr - z.arr)) as [WarCapSeat[], WarCapSeat[]];
}
export const WAR_LINE_M = 16_000;
/** The walls close over the first six minutes (16 km → ~13.5 km stand-off). */
export function warLineD(t: number): number { return WAR_LINE_M - Math.min(t, 360) * 7; }
/** A capital's ANCHOR at war-time t (wall drift + a 500 m sway) — absolute. */
export function warCapAnchor(frame: { ax: V3; az: V3 }, centre: V3, side: number, seat: WarCapSeat, t: number, out: V3): V3 {
  const sgn = side === 0 ? 1 : -1;
  const bx = sgn * (warLineD(t) + seat.dx + Math.sin(t * 0.05 + seat.sway) * 500);
  out.x = centre.x + frame.ax.x * bx + frame.az.x * seat.bz;
  out.y = centre.y + seat.by;
  out.z = centre.z + frame.ax.z * bx + frame.az.z * seat.bz;
  return out;
}
/** THE DREADNOUGHT KINEMATICS (the renderer's step, run in its 60 Hz FixedSim): seek the drifting anchor at ≤ 48 m/s
 *  with heavy inertia, separate from every other living hull inside 5.6 km. Each client runs its own; the SERVER never
 *  does — it derives the anchor and allows `KIN_TOL_M` for this relaxation (measured by verify:war-authority over full
 *  wars with deaths). Three.js Vector3 arithmetic, op for op (normalize = × 1/len). */
export const WAR_CAP_SPEED = 48;
export const WAR_CAP_SEP = 5600;
export function capKinStep(kin: { p: V3; v: V3 }, anchor: V3, others: readonly V3[], dt: number): void {
  const p = kin.p, v = kin.v;
  let dx = anchor.x - p.x, dy = anchor.y - p.y, dz = anchor.z - p.z;
  const toAnchor = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const inv = 1 / (toAnchor || 1);
  dx *= inv; dy *= inv; dz *= inv;
  const sp = Math.min(WAR_CAP_SPEED, toAnchor * 0.05);
  dx *= sp; dy *= sp; dz *= sp;
  for (const o of others) {
    const ex = p.x - o.x, ey = p.y - o.y, ez = p.z - o.z;
    const d = Math.sqrt(ex * ex + ey * ey + ez * ez);
    if (d > 1 && d < WAR_CAP_SEP) {
      const k = 1 / d, s = (WAR_CAP_SEP - d) * 0.03;
      dx += ex * k * s; dy += ey * k * s; dz += ez * k * s;
    }
  }
  const a = Math.min(1, dt * 0.35);
  v.x += (dx - v.x) * a; v.y += (dy - v.y) * a; v.z += (dz - v.z) * a;
  p.x += v.x * dt; p.y += v.y * dt; p.z += v.z * dt;
}
/** A capital is a warp signature, not a hull, for its first WARP_STRETCH_S seconds (no collider, no damage). */
export const WARP_STRETCH_S = 0.7;
/** An escort's warp-in second (the renderer's stagger: 3–12 s after the walls). */
export function escortArrival(cyc: number, side: number, cls: EscortClass, slot: number): number {
  return 3 + warH(`war${side}:${ESCORT_KEY[cls]}${slot}arr${cyc}`) * 9;
}

// ── THE CERTIFIED PROXIES AND HP (independent of any GLB, tier or asset) ─────────────────────────────────────────
/** A capital: an oriented box in the war frame — along the wall axis (the hull's long axis), up (world Y) and lateral.
 *  Covers both cooked masters (wrapped to 4,500 m: a 900 × 1,302 m and a 1,184 × 2,037 m cross-section, recentred)
 *  and the procedural silhouette. */
export const CAP_PROXY = { halfLen: 2250, halfUp: 1050, halfSide: 620 } as const;
/** The measured envelope of the client's capital relaxation around the anchor (verify:war-authority § KINEMATICS:
 *  the largest |kin − anchor| over full 60 Hz wars with deaths, plus a clock-skew and latency margin). */
export const KIN_TOL_M = 1200;
/** Certified hit points of a named life: the renderer's WAR_CAP_HP; an escort = the tier-3 hull (5) × its class. */
export const LIFE_HP: Record<'capital' | EscortClass, number> = { capital: 520, frigate: 80, corvette: 20 };
export type LifeKind = 'capital' | EscortClass;
/** The roster key of a life (`cap<side>:<slot>`, `war<side>:fri<slot>`, `war<side>:cor<slot>`). */
export function lifeRosterKey(kind: LifeKind, side: number, slot: number): string {
  return kind === 'capital' ? `cap${side}:${slot}` : `war${side}:${ESCORT_KEY[kind]}${slot}`;
}
export const LIFE_KEY_RE = /^(cap[01]:\d{1,2}|war[01]:(fri|cor)\d{1,2})$/;
export function parseLifeKey(key: string): { kind: LifeKind; side: 0 | 1; slot: number } | null {
  if (!LIFE_KEY_RE.test(key)) return null;
  const side = (key.charAt(3) === '1' ? 1 : 0) as 0 | 1;
  if (key.startsWith('cap')) return { kind: 'capital', side, slot: Number(key.slice(5)) };
  const m = /^war[01]:(fri|cor)(\d+)$/.exec(key)!;
  return { kind: m[1] === 'fri' ? 'frigate' : 'corvette', side, slot: Number(m[2]) };
}
/** The public table's key of a life: `<campaignId>:<rosterKey>`. */
export function warLifeKey(campaignId: bigint, rosterKey: string): string { return `${campaignId}:${rosterKey}`; }

// ── THE WEAPONS (the renderer's constants — verify:war-authority pins them against SpaceModule) ─────────────────
export type WarMount = 'gun' | 'missile';
export const WAR_MOUNTS: readonly WarMount[] = ['gun', 'missile'];
/** The twin cannons: two bolts a volley, 420 m/s on top of the ship's own velocity, 1.6 s of flight, a 0.16 s volley
 *  cooldown × the fit, 9 capacitor a volley. A bolt does 12 × weapon on a capital, 1 × weapon on a hull. */
export const GUN = { boltSpeed: 420, lifeS: 1.6, cooldownS: 0.16, boltsPerVolley: 2, capPerVolley: 9, dmgCapital: 12, dmgHull: 1 } as const;
/** The homing rack: 300 m/s + the ship, 9 s of flight, one every 2.5 s; a warhead does 90 to a capital (flat), 6 ×
 *  weapon to a hull. The rack holds `missiles` (fit) and rearms at a station. */
export const MISSILE = { speed: 300, lifeS: 9, coolS: 2.5, dmgCapital: 90, dmgHull: 6 } as const;
/** The capacitor's regen (the renderer's CAP_REGEN) and the WEAPONS power preset's capacitor multiplier — the bound. */
export const CAP_REGEN = 16;
export const POWER_CAP_MAX = 1.6;
/** A rack refills only at a station; the server bounds a pilot's warheads by one full rack per REARM_S. */
export const REARM_S = 60;
/** Server damage for one accepted hit. */
export function hitDamage(kind: LifeKind, mount: WarMount, weaponMult: number): number {
  if (kind === 'capital') return mount === 'gun' ? GUN.dmgCapital * weaponMult : MISSILE.dmgCapital;
  return mount === 'gun' ? GUN.dmgHull * weaponMult : MISSILE.dmgHull * weaponMult;
}

// ── THE FIT (the combat subset of client/src/lib/fleet.ts shipStats — verify:war-authority pins equality) ────────
/** The chassis columns combat reads (client CHASSIS maxSpeed / boost / capacitor). */
export const CHASSIS_COMBAT: Record<string, { maxSpeed: number; boost: number; capacitor: number }> = {
  ship_lancer: { maxSpeed: 105, boost: 1, capacitor: 100 },
  ship_talon: { maxSpeed: 132, boost: 1.25, capacitor: 85 },
  ship_courier: { maxSpeed: 96, boost: 0.9, capacitor: 80 },
  ship_bulwark: { maxSpeed: 84, boost: 0.8, capacitor: 150 },
  ship_sovereign: { maxSpeed: 78, boost: 0.7, capacitor: 200 },
  ship_wren: { maxSpeed: 118, boost: 1.1, capacitor: 70 },
  ship_packet: { maxSpeed: 110, boost: 1.15, capacitor: 70 },
  ship_pathfinder: { maxSpeed: 100, boost: 1, capacitor: 130 },
  ship_kestrel: { maxSpeed: 150, boost: 1.5, capacitor: 70 },
  ship_warden: { maxSpeed: 98, boost: 0.95, capacitor: 120 },
  ship_prospector: { maxSpeed: 90, boost: 0.85, capacitor: 160 },
  ship_gleaner: { maxSpeed: 94, boost: 0.9, capacitor: 110 },
  ship_harrier: { maxSpeed: 124, boost: 1.15, capacitor: 120 },
  ship_vesper: { maxSpeed: 116, boost: 1.1, capacitor: 150 },
  ship_farstrider: { maxSpeed: 108, boost: 1.3, capacitor: 140 },
  ship_arbalest: { maxSpeed: 88, boost: 0.85, capacitor: 230 },
  ship_drover: { maxSpeed: 82, boost: 0.8, capacitor: 90 },
  ship_tender: { maxSpeed: 92, boost: 1, capacitor: 110 },
  ship_delver: { maxSpeed: 66, boost: 0.6, capacitor: 220 },
  ship_laurel: { maxSpeed: 96, boost: 0.9, capacitor: 140 },
  ship_rime: { maxSpeed: 104, boost: 1, capacitor: 170 },
  ship_ironclad: { maxSpeed: 76, boost: 0.75, capacitor: 170 },
  ship_bastion: { maxSpeed: 80, boost: 0.75, capacitor: 180 },
  ship_argosy: { maxSpeed: 62, boost: 0.6, capacitor: 110 },
  ship_reclaimer: { maxSpeed: 70, boost: 0.65, capacitor: 150 },
};
export const STOCK_CHASSIS = 'ship_lancer';
export interface FitComp { slot: string; quality: number }
export interface WarFit { maxSpeed: number; boost: number; capacitor: number; weaponMult: number; fireCdMult: number; regenMult: number; missiles: number }
const qMult = (q: number, span: number): number => 1 + Math.max(0, Math.min(1000, q)) / 1000 * span;
export function warFitStats(chassisKey: string, comps: readonly FitComp[], skills: { gunnery: number; navigation: number }, effect: string): WarFit {
  const c = CHASSIS_COMBAT[chassisKey] ?? CHASSIS_COMBAT[STOCK_CHASSIS];
  const q = (slot: string): number => comps.find((x) => x.slot === slot)?.quality ?? -1;
  const has = (slot: string): boolean => q(slot) >= 0;
  const f: WarFit = {
    maxSpeed: c.maxSpeed * (has('engine') ? qMult(q('engine'), 0.35) : 1),
    boost: c.boost,
    capacitor: Math.round(c.capacitor * (has('capacitor') ? qMult(q('capacitor'), 0.7) : 1)),
    weaponMult: has('weapon') ? qMult(q('weapon'), 0.9) : 1,
    fireCdMult: has('weapon') ? 1 / qMult(q('weapon'), 0.35) : 1,
    regenMult: has('reactor') ? qMult(q('reactor'), 0.6) : 1,
    missiles: has('missile') ? 2 + Math.floor(q('missile') / 250) : 0,
  };
  f.maxSpeed *= 1 + 0.03 * skills.navigation;
  f.weaponMult *= 1 + 0.04 * skills.gunnery;
  if (effect === 'magnetar') f.weaponMult *= 1.2;
  else if (effect === 'cataclysm') f.capacitor = Math.round(f.capacitor * 0.75);
  return f;
}

// ── THE THEATRE: movement honesty inside the war zone (a validated ENVELOPE, not server-simulated movement) ─────
/** Inside a PINNED live war this far from its centre (the receipt zone), a position write is bounded by the hull. */
export const WAR_THEATRE_M = WAR_ZONE_M;
/** The hyperdrive will not run inside the theatre (THE MASS LOCK — the client drops a jump at this radius). */
export const MASS_LOCK_M = WAR_ZONE_M + 2_000;
/** The renderer's flight ceilings: the ENGINES power preset's speed (1.15), the booster (2.5 × chassis boost) and the
 *  overcruise wind-up (×10) — the fastest combat movement a hull can make (the hyperdrive excluded). */
export const POWER_SPEED_MAX = 1.15;
export const BOOST_MULT = 2.5;
export const CRUISE_MAX = 10;
export const TOP_SPEED_SLACK = 1.1;
export function combatTopSpeed(fit: WarFit): number {
  return fit.maxSpeed * POWER_SPEED_MAX * Math.max(BOOST_MULT * fit.boost, CRUISE_MAX) * TOP_SPEED_SLACK;
}
export const MOVE_SLACK_M = 30;
/** One write inside the theatre: the claim when it fits the banked seconds at the top speed (+ slack), else CLAMPED
 *  toward it (a refusal from a stale row cascades — the MOVEMENT BUDGET's lesson). `usedS` = the seconds it spends. */
export function theatreMove(row: V3, claim: V3, vTop: number, availS: number): { pos: V3; clamped: boolean; usedS: number } {
  const dx = claim.x - row.x, dy = claim.y - row.y, dz = claim.z - row.z;
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const cap = vTop * Math.max(0, availS) + MOVE_SLACK_M * Math.min(1, Math.max(0, availS));
  if (d <= cap) return { pos: { x: claim.x, y: claim.y, z: claim.z }, clamped: false, usedS: vTop > 0 ? d / vTop : 0 };
  const k = d > 0 ? cap / d : 0;
  return { pos: { x: row.x + dx * k, y: row.y + dy * k, z: row.z + dz * k }, clamped: true, usedS: Math.max(0, availS) };
}

// ── THE SHOT JUDGE (pure; the reducer feeds it server facts and the intent's claims) ───────────────────────────────
/** The longest a shot may be reported after it was fired: the projectile's flight + a latency allowance (Source clamps
 *  its rewind with sv_maxunlag; here the ceiling is the weapon's own flight, beyond which no projectile exists). */
export const LATENCY_S = 0.6;
export function ageMaxS(mount: WarMount): number { return (mount === 'gun' ? GUN.lifeS : MISSILE.lifeS) + LATENCY_S; }
/** How far a row may lag behind a ship that streams 5 times a second (a silent client is judged on a 2 s row). */
export const ROW_STALE_CAP_S = 2;
export const ORIGIN_SLACK_M = 80;
export const REACH_SLACK_M = 60;
/** The anchor moves ≤ ~32 m/s (sway 25 + the closing walls 7); a report's latency and the clock's skew are covered. */
export const ANCHOR_DRIFT_MPS = 32;
export interface ShotFacts {
  kind: LifeKind; mount: WarMount; ageS: number;
  origin: V3; impact: V3;
  row: V3; rowStaleS: number;
  vTop: number;
  centre: V3;
  /** Capitals: the target's server-derived anchor at the report and the wall axis. */
  anchor?: V3; ax?: V3;
}
export type ShotVerdict = { ok: false; why: string } | { ok: true; hit: boolean; why: string };
const dist = (a: V3, b: V3): number => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y) + (a.z - b.z) * (a.z - b.z));
export function finiteV3(v: V3): boolean { return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z); }
/** ok:false = an IMPOSSIBLE claim (refused, nothing consumed); ok:true,hit:false = a judged MISS (the shot is spent). */
export function judgeWarShot(f: ShotFacts): ShotVerdict {
  if (!finiteV3(f.origin) || !finiteV3(f.impact) || !Number.isFinite(f.ageS)) return { ok: false, why: 'Numbers must be finite' };
  if (f.ageS < 0 || f.ageS > ageMaxS(f.mount)) return { ok: false, why: `That shot's time is outside the window (${f.ageS.toFixed(2)} s; ≤ ${ageMaxS(f.mount).toFixed(1)} s)` };
  const stale = Math.min(Math.max(0, f.rowStaleS), ROW_STALE_CAP_S);
  const oTol = f.vTop * (f.ageS + stale + LATENCY_S) + ORIGIN_SLACK_M;
  const oD = dist(f.origin, f.row);
  if (oD > oTol) return { ok: false, why: `Your shot left from where your ship has not been (${Math.round(oD)} m from your record; ≤ ${Math.round(oTol)} m)` };
  const speed = (f.mount === 'gun' ? GUN.boltSpeed : MISSILE.speed) + f.vTop;
  const reach = speed * Math.min(f.ageS, f.mount === 'gun' ? GUN.lifeS : MISSILE.lifeS) + REACH_SLACK_M;
  const iD = dist(f.impact, f.origin);
  if (iD > reach) return { ok: false, why: `No ${f.mount === 'gun' ? 'bolt' : 'warhead'} flies that far (${Math.round(iD)} m in ${f.ageS.toFixed(2)} s; ≤ ${Math.round(reach)} m)` };
  if (dist(f.impact, f.centre) > WAR_THEATRE_M + 2_000) return { ok: false, why: 'That impact is outside the war zone' };
  if (f.kind !== 'capital') return { ok: true, hit: true, why: 'a client-flown escort, inside the theatre and within your reach' };
  if (!f.anchor || !f.ax) return { ok: false, why: 'No seat for that capital' };
  // The certified box in the war frame (ax, up, lateral), grown by the measured kinematic envelope and the drift.
  const tol = KIN_TOL_M + ANCHOR_DRIFT_MPS * LATENCY_S;
  const qx = f.impact.x - f.anchor.x, qy = f.impact.y - f.anchor.y, qz = f.impact.z - f.anchor.z;
  const along = qx * f.ax.x + qz * f.ax.z;
  const side = -qx * f.ax.z + qz * f.ax.x; // az = (−ax.z, 0, ax.x)
  const inside = Math.abs(along) <= CAP_PROXY.halfLen + tol && Math.abs(qy) <= CAP_PROXY.halfUp + tol && Math.abs(side) <= CAP_PROXY.halfSide + tol;
  return inside ? { ok: true, hit: true, why: 'inside the certified hull' } : { ok: true, hit: false, why: 'outside the certified hull at its server seat' };
}

// ── THE RECEIVE WINDOW (a 64-wide anti-replay bitmap, RFC 4303 style + the payload hash of each recent sequence) ───
export const SEQ_WINDOW = 64;
export const SEQ_HASHES = 64;
export interface StreamWin { hi: bigint; mask: bigint; hashes: string }
export type SeqVerdict = 'new' | 'dup' | 'reused' | 'stale';
const MASK64 = (1n << 64n) - 1n;
function hashOf(hashes: string, seq: bigint): string | null {
  const k = `${seq}:`;
  for (const part of hashes ? hashes.split(',') : []) if (part.startsWith(k)) return part.slice(k.length);
  return null;
}
/** Judge a sequence: new (accept; returns the advanced window), dup (an exact duplicate — succeed with no effect),
 *  reused (a used sequence with a DIFFERENT payload — refuse), stale (below the window — refuse). */
export function streamAccept(w: StreamWin, seq: bigint, hash: string): { verdict: SeqVerdict; next: StreamWin } {
  if (seq <= 0n) return { verdict: 'stale', next: w };
  if (w.hi > 0n && seq <= w.hi) {
    const off = w.hi - seq;
    if (off >= BigInt(SEQ_WINDOW)) return { verdict: 'stale', next: w };
    if ((w.mask >> off) & 1n) {
      const h = hashOf(w.hashes, seq);
      return { verdict: h === hash ? 'dup' : h === null ? 'stale' : 'reused', next: w };
    }
    return { verdict: 'new', next: { hi: w.hi, mask: w.mask | (1n << off), hashes: pushHash(w.hashes, seq, hash) } };
  }
  const shift = w.hi > 0n ? seq - w.hi : BigInt(SEQ_WINDOW);
  const mask = shift >= BigInt(SEQ_WINDOW) ? 1n : (((w.mask << shift) & MASK64) | 1n);
  return { verdict: 'new', next: { hi: seq, mask, hashes: pushHash(w.hashes, seq, hash) } };
}
function pushHash(hashes: string, seq: bigint, hash: string): string {
  const parts = hashes ? hashes.split(',') : [];
  parts.push(`${seq}:${hash}`);
  return parts.slice(-SEQ_HASHES).join(',');
}

// ── THE RATE BUCKETS (token buckets on the SERVER's clock: the sustained rate is the fit's, the burst bounded) ─────
export interface Bucket { tokens: number; atS: number }
/** Refill to `cap` at `rate`/s since `atS`, then take `cost`. A clock stepped back is no time passed, never a debt. */
export function bucketTake(b: Bucket, nowS: number, rate: number, cap: number, cost: number): { ok: boolean; next: Bucket } {
  const el = Math.max(0, nowS - b.atS);
  const tokens = Math.min(cap, b.tokens + el * rate);
  if (tokens + 1e-9 < cost) return { ok: false, next: { tokens, atS: nowS } };
  return { ok: true, next: { tokens: tokens - cost, atS: nowS } };
}
/** Bolts a second the fit can put out, and the bucket's burst (two volleys). */
export function gunBoltRate(fit: WarFit): number { return GUN.boltsPerVolley / (GUN.cooldownS * fit.fireCdMult); }
export const GUN_BURST_BOLTS = 4;
/** Capacitor a bolt costs and the capacitor's regen bound (the WEAPONS preset's 1.6). */
export function capRegenBound(fit: WarFit): number { return CAP_REGEN * fit.regenMult * POWER_CAP_MAX; }
export const CAP_PER_BOLT = GUN.capPerVolley / GUN.boltsPerVolley;

// ── RECEIPTS ─────────────────────────────────────────────────────────────────────────────────────────────────────
/** A distinct pilot whose accepted damage reached this share of the life's hp earns a bounded ASSIST (no death, no
 *  kill credit, no bounty); at most ASSIST_MAX of them, the largest damage first. */
export const ASSIST_MIN_FRAC = 0.1;
export const ASSIST_MAX = 3;
export const ASSIST_POINTS: Record<LifeKind, number> = { capital: 2, frigate: 1, corvette: 0 };
/** Gunships stay a POOL with claim-based receipts (no server body yet — report §9.3 last ¶, space.md gap 29): in a
 *  pinned war they are CAPPED per pilot and labelled claim-based; no objective may rest on them. */
export const GUNSHIP_CLAIM_CAP = 10;
/** The payload a sequence binds (a different payload under a used sequence is refused). */
export function shotPayloadKey(p: { campaignId: bigint; target: string; life: number; mount: string; ageMs: number; origin: V3; impact: V3; version: number }): string {
  const c = (v: number): string => (Number.isFinite(v) ? Math.round(v * 100) : 'x').toString();
  return [p.campaignId, p.target, p.life, p.mount, p.ageMs, c(p.origin.x), c(p.origin.y), c(p.origin.z), c(p.impact.x), c(p.impact.y), c(p.impact.z), p.version].join('|');
}
/** Whether a war is past its last shot: the finale's 32 s take no fire (the lines warp out). */
export function finaleBegun(nowS: number, endsAtS: number): boolean { return nowS >= endsAtS - BATTLE_END_S; }
